import {getRouterSelectors} from '@ngrx/router-store';
import {createSelector} from '@ngrx/store';
import {AppPlatform, AppPlatformIcons} from '../../constants/platform';
import {
	AppOrigin,
	QUERY_ACCOUNT_ID,
	QUERY_APP_TOKEN,
	QUERY_ERROR,
	QUERY_FROM_ORIGIN,
	QUERY_ID_TOKEN,
	QUERY_REDIRECT_URL,
	QUERY_SESSION_TOKEN,
	QUERY_STATE,
	ROUTE_PARAM_PLATFORM_NAME
} from '../../constants/url-params';
import {coerceValue} from '@kamp-n/ng-common-tools';
import {decodedUserInfo, UserInfo} from '../../utils/decode-utils';

export interface SessionParams {
	accountId: string | null;
	redirectUrl: string;
	sessionToken: string;
	platformName: AppPlatform;
}

export const {
	selectCurrentRoute, // select the current route
	selectFragment, // select the current route fragment
	selectQueryParams, // select the current route query params
	selectQueryParam, // factory function to select a query param
	selectRouteParams, // select the current route params
	selectRouteParam, // factory function to select a route param
	selectRouteData, // select the current route data
	selectRouteDataParam, // factory function to select a route data param
	selectUrl, // select the current url
	selectTitle, // select the title if available
} = getRouterSelectors();

export const selectRequiredSessionParams = createSelector(
	selectQueryParam(QUERY_REDIRECT_URL),
	selectQueryParam(QUERY_SESSION_TOKEN),
	selectRouteParam(ROUTE_PARAM_PLATFORM_NAME),
	selectQueryParam(QUERY_ACCOUNT_ID),
	(redirectUrl, sessionToken, platformName, accountId): SessionParams => (
		{
			accountId: accountId as string ?? null,
			redirectUrl: redirectUrl as string,
			sessionToken: sessionToken as string,
			platformName: platformName as AppPlatform
		}
	)
);

export const selectOrigin = createSelector(
	selectQueryParam(QUERY_FROM_ORIGIN),
	(origin): AppOrigin => coerceValue(origin, Object.values(AppOrigin), AppOrigin.KAMPN)
);

export const selectPlatformName = createSelector(
	selectRouteParam(ROUTE_PARAM_PLATFORM_NAME),
	(platform): AppPlatform => coerceValue(platform, Object.values(AppPlatform))
);

export const selectPlatformImage = createSelector(
	selectPlatformName,
	(platformName): string => {
		const platform = platformName as AppPlatform;
		return AppPlatformIcons[platform];
	}
);

export const selectUserInfo = createSelector(
	selectQueryParam(QUERY_ID_TOKEN),
	(encodedUserInfo): UserInfo | null => decodedUserInfo(encodedUserInfo as string) ?? null
);

export const selectErrorCode = createSelector(
	selectQueryParam(QUERY_ERROR),
	(error): string | null => error as string ?? null
);

export const selectRequiredRedirectionParams = createSelector(
	selectQueryParam(QUERY_REDIRECT_URL),
	selectQueryParam(QUERY_APP_TOKEN),
	selectQueryParam(QUERY_SESSION_TOKEN),
	(redirectUrl, appToken, sessionToken) => ({
		sessionToken: sessionToken as string,
		redirectUrl: redirectUrl as string,
		appToken: appToken as string
	})
);

export const selectAppToken = createSelector(
	selectRouteParam(QUERY_APP_TOKEN),
	(appToken): string | null => appToken ?? null
);

export const selectStateParam = createSelector(
	selectQueryParam(QUERY_STATE),
	(stateParam): string | null => stateParam as string ?? null
);
